class PredictiveSearch extends HTMLElement {
	constructor() {
		super()

		this.form = this.querySelector('form')
		this.input = this.querySelector('input[name="q"]')
		this.results = this.querySelector('.predictive-search-results')
		this.resultsInner = this.querySelector('[data-predictive-search-results]')
		this.limit = this.dataset.limit || '6'
		this.cache = new Map()
		this.abortController = null

		this.input.addEventListener(
			'input',
			this.debounce(() => this.onChange(), 300)
		)
		this.form.addEventListener('reset', () => this.close(true))

		const container = this.closest('.search-modal, [data-mobile-menu-drawer]')
		if (container) {
			new MutationObserver(() => {
				const isOpen =
					container.classList.contains('active') ||
					container.getAttribute('aria-hidden') === 'false'
				if (!isOpen) {
					this.form.reset()
				}
			}).observe(container, {
				attributes: true,
				attributeFilter: ['class', 'aria-hidden']
			})
		}
	}

	onChange() {
		const searchTerm = this.input.value.trim()
		if (!searchTerm.length) {
			this.close(true)
			return
		}
		this.getSearchResults(searchTerm)
	}

	getSearchResults(searchTerm) {
		const cacheKey = searchTerm.toLowerCase()
		if (this.cache.has(cacheKey)) {
			this.render(this.cache.get(cacheKey))
			return
		}

		this.abortController?.abort()
		this.abortController = new AbortController()

		const params = new URLSearchParams({
			q: searchTerm,
			section_id: 'predictive-search',
			'resources[type]': 'product',
			'resources[limit]': this.limit,
			'resources[options][fields]': 'title,product_type,variants.title,tag'
		})
		const url = window.routes?.predictive_search_url || '/search/suggest'

		fetch(`${url}?${params.toString()}`, {
			signal: this.abortController.signal
		})
			.then((response) => {
				if (!response.ok) {
					throw new Error(response.status)
				}
				return response.text()
			})
			.then((text) => {
				const markup = new DOMParser()
					.parseFromString(text, 'text/html')
					.querySelector('#shopify-section-predictive-search').innerHTML
				this.cache.set(cacheKey, markup)
				if (this.input.value.trim().toLowerCase() === cacheKey) {
					this.render(markup)
				}
			})
			.catch((error) => {
				if (error.name === 'AbortError') return
				this.close()
			})
	}

	render(markup) {
		this.resultsInner.innerHTML = markup
		this.open()
	}

	open() {
		this.results.classList.add('is-open')
		this.input.setAttribute('aria-expanded', 'true')
	}

	close(clearResults = false) {
		this.abortController?.abort()
		this.results.classList.remove('is-open')
		this.input.setAttribute('aria-expanded', 'false')
		if (clearResults) {
			this.results.addEventListener(
				'transitionend',
				() => {
					if (!this.results.classList.contains('is-open')) {
						this.resultsInner.innerHTML = ''
					}
				},
				{ once: true }
			)
		}
	}

	debounce(fn, wait) {
		let t
		return (...args) => {
			clearTimeout(t)
			t = setTimeout(() => fn.apply(this, args), wait)
		}
	}
}

customElements.define('predictive-search', PredictiveSearch)
